import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PotentialCause } from '../../../../models/potential-cause.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  EditListModalBase,
  EditListSchema,
} from '../../../../shared/bases/edit-list-modal.base';
import {
  PercentageAdjustmentUtils,
  AdjustmentItem,
  PercentageAdjustmentResult,
} from '../../../../../utils/percentage-adjustment.utils';

@Component({
  selector: 'app-edit-potential-causes-modal',
  templateUrl: './edit-potential-causes-modal.component.html',
  styleUrls: ['./edit-potential-causes-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class EditPotentialCausesModalComponent
  extends EditListModalBase<PotentialCause>
  implements OnInit, OnChanges, OnDestroy
{
  @Output() onSubmitForm = new EventEmitter<any>();
  @Output() onCancelForm = new EventEmitter<void>();

  showAdjustmentDialog = false;
  adjustmentData: PercentageAdjustmentResult | null = null;

  constructor(
    fb: FormBuilder,
    confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {
    super(
      fb,
      {
        arrayName: 'potentialCauses',
        confirmDeleteHeader: 'Confirmation de suppression',
        confirmDeleteMessage:
          'Êtes-vous sûr de vouloir supprimer cette cause potentielle ?',
        fields: {
          percentage: {
            defaultValue: '',
            validators: [Validators.required, Validators.minLength(1)],
            trackOriginal: true,
          },
        },
        sort: (a: PotentialCause, b: PotentialCause) => {
          const ap = a.percentage ?? -Infinity;
          const bp = b.percentage ?? -Infinity;
          if (ap !== bp) return bp - ap;
          return a.description.localeCompare(b.description);
        },
      } as EditListSchema<PotentialCause>,
      confirmationService,
    );
  }

  get potentialCausesArray(): FormArray {
    return this.form.get('potentialCauses') as FormArray;
  }

  private validatePercentages(): boolean {
    return PercentageAdjustmentUtils.validateTotalPercentage(
      this.potentialCausesArray.controls,
    );
  }

  private getPercentageAdjustments(): PercentageAdjustmentResult | null {
    return PercentageAdjustmentUtils.calculatePercentageAdjustments(
      this.potentialCausesArray.controls,
    );
  }

  override get isFormValid(): boolean {
    const activeCauses = this.potentialCausesArray.controls.filter(
      (control) => !control.get('isDeleted')?.value,
    );
    const allValid = activeCauses.every((control) => control.valid);
    const percentagesValid = this.validatePercentages();
    const hasActiveCauses = activeCauses.length > 0;
    return allValid && percentagesValid && hasActiveCauses;
  }

  get isSubmissionAllowed(): boolean {
    const activeCauses = this.potentialCausesArray.controls.filter(
      (control) => !control.get('isDeleted')?.value,
    );
    const allValid = activeCauses.every((control) => control.valid);
    const hasActiveCauses = activeCauses.length > 0;
    return allValid && hasActiveCauses;
  }

  override get validationMessage(): string {
    const activeCauses = this.potentialCausesArray.controls.filter(
      (control) => !control.get('isDeleted')?.value,
    );

    if (activeCauses.length === 0) {
      return 'Au moins une cause potentielle est requise.';
    }

    const totalPercentage = activeCauses.reduce((sum, control) => {
      const percentage = control.get('percentage')?.value || 0;
      return sum + percentage;
    }, 0);

    if (totalPercentage !== 100) {
      return `La somme des pourcentages doit être égale à 100%. Actuellement: ${totalPercentage}%`;
    }

    const hasInvalidFields = activeCauses.some((control) => control.invalid);
    if (hasInvalidFields) {
      return 'Veuillez corriger les erreurs dans le formulaire.';
    }

    return '';
  }

  onSubmit(): void {
    const activeCauses = this.potentialCausesArray.controls.filter(
      (control) => !control.get('isDeleted')?.value,
    );

    if (activeCauses.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Au moins une cause potentielle est requise.',
      });
      return;
    }

    const hasInvalidFields = activeCauses.some((control) => control.invalid);
    if (hasInvalidFields) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Veuillez corriger les erreurs dans le formulaire.',
      });
      return;
    }

    const adjustments = this.getPercentageAdjustments();

    if (adjustments === null) {
      this.onSubmitForm.emit(this.buildSubmitResult());
      this.closeModal();
      return;
    }

    this.showPercentageAdjustmentConfirmation(adjustments);
  }

  private showPercentageAdjustmentConfirmation(
    adjustments: PercentageAdjustmentResult,
  ): void {
    this.adjustmentData = adjustments;
    this.showAdjustmentDialog = true;
  }

  acceptAdjustment(): void {
    if (this.adjustmentData) {
      this.applyPercentageAdjustments(this.adjustmentData);
      this.onSubmitForm.emit(this.buildSubmitResult());
      this.closeModal();
    }
    this.showAdjustmentDialog = false;
    this.adjustmentData = null;
  }

  cancelAdjustment(): void {
    this.showAdjustmentDialog = false;
    this.adjustmentData = null;
  }

  private applyPercentageAdjustments(
    adjustments: PercentageAdjustmentResult,
  ): void {
    PercentageAdjustmentUtils.applyAdjustments(adjustments);
  }

  hasChangedItemsBeingAdjusted(): boolean {
    if (!this.adjustmentData) return false;

    return this.adjustmentData.changedItems.some(
      (item) =>
        item.newValue !== undefined &&
        Math.abs(item.newValue - item.currentValue) > 0.01,
    );
  }

  onCancel(): void {
    this.onCancelForm.emit();
    this.closeModal();
  }
}
